import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

interface WebVitalPayload {
  name: string;
  value: number;
  id: string;
  rating: string;
  navigationType: string;
  url: string;
  timestamp: number;
}

const ENDPOINT = '/api/v1/metrics/web-vitals';

/**
 * Forwards a Web Vital sample to the backend if the endpoint exists;
 * otherwise logs to the console. Prefers `navigator.sendBeacon` so the
 * report survives page unload (LCP / CLS are typically captured on the
 * `visibilitychange` hidden path). Falls back to `fetch(keepalive: true)`,
 * then to `console.log` when neither is usable (SSR, disabled networking).
 */
export function reportWebVital(metric: Metric): void {
  const payload: WebVitalPayload = {
    name: metric.name,
    value: metric.value,
    id: metric.id,
    rating: metric.rating,
    navigationType: metric.navigationType,
    url: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: Date.now(),
  };
  const body = JSON.stringify(payload);

  if (typeof navigator === 'undefined') {
    if (typeof console !== 'undefined') {
      console.log('[web-vital]', payload);
    }
    return;
  }

  if (typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    } catch {
      // fall through to fetch
    }
  }

  if (typeof fetch === 'function') {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      console.log('[web-vital]', payload);
    });
    return;
  }

  console.log('[web-vital]', payload);
}

/**
 * Subscribes to all 5 Core Web Vitals and forwards each sample to the
 * backend. Idempotent across HMR reloads thanks to the module-level guard.
 *
 * web-vitals fires each metric possibly multiple times per navigation
 * (deltas), but we always send the full current snapshot — backend dedupes
 * by `id` if needed.
 */
let initialized = false;
export function initWebVitals(): void {
  if (initialized) return;
  initialized = true;

  onCLS(reportWebVital);
  onLCP(reportWebVital);
  onINP(reportWebVital);
  onTTFB(reportWebVital);
  onFCP(reportWebVital);
}
